import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, X } from 'lucide-react';

// Дефолтный центр — Ташкент (как в Map.js)
const DEFAULT_CENTER = [41.299, 69.240];

const pinIcon = L.divIcon({
  className: '',
  html: '<div class="map-marker-pin"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

/**
 * Выбор точки на карте: клик ставит пин, поиск по адресу через Nominatim (OSM).
 * Контролируемый: value = { lat, lng } | null, onChange(coords|null).
 * Используется в модалках создания киоска и редактирования локации.
 */
export const MapPicker = ({ value, onChange, height = 240 }) => {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const markerRef    = useRef(null);
  const onChangeRef  = useRef(onChange);
  onChangeRef.current = onChange;

  const [query, setQuery]       = useState('');
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState('');

  // Инициализация карты один раз
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: value ? [value.lat, value.lng] : DEFAULT_CENTER,
      zoom: value ? 16 : 12,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);

    map.on('click', (e) => {
      onChangeRef.current({ lat: +e.latlng.lat.toFixed(6), lng: +e.latlng.lng.toFixed(6) });
    });

    mapRef.current = map;
    // Карта внутри модалки: контейнер получает размер после рендера —
    // без invalidateSize Leaflet рисует тайлы только в углу.
    setTimeout(() => map.invalidateSize(), 50);

    return () => { map.remove(); mapRef.current = null; markerRef.current = null; };
  }, []); // eslint-disable-line

  // Синхронизация пина с value
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
    if (value) {
      markerRef.current = L.marker([value.lat, value.lng], { icon: pinIcon }).addTo(map);
    }
  }, [value]);

  // Поиск адреса через Nominatim — двигает карту и ставит пин (можно уточнить кликом)
  const handleSearch = useCallback(async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setSearchErr('');
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`,
        { headers: { 'Accept-Language': 'ru' } }
      );
      const results = await res.json();
      if (results.length === 0) { setSearchErr('Адрес не найден'); return; }
      const { lat, lon } = results[0];
      const coords = { lat: +parseFloat(lat).toFixed(6), lng: +parseFloat(lon).toFixed(6) };
      onChangeRef.current(coords);
      mapRef.current?.setView([coords.lat, coords.lng], 17);
    } catch {
      setSearchErr('Ошибка поиска — попробуйте ещё раз');
    } finally {
      setSearching(false);
    }
  }, [query]);

  return (
    <div>
      {/* Поиск по адресу */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Поиск по адресу (например: Ташкент, Афросиаб 41)"
          style={{ flex: 1, fontSize: '13px' }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
        />
        <button type="button" className="btn" onClick={handleSearch} disabled={searching} style={{ flexShrink: 0 }}>
          <Search size={13} /> {searching ? '...' : 'Найти'}
        </button>
      </div>
      {searchErr && <p style={{ fontSize: '12px', color: 'var(--yellow)', marginBottom: '6px' }}>{searchErr}</p>}

      {/* Карта */}
      <div
        ref={containerRef}
        style={{ height: `${height}px`, borderRadius: '10px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}
      />

      {/* Текущая точка */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', minHeight: '24px' }}>
        {value ? (
          <>
            <span className="mono" style={{ fontSize: '12px', color: 'var(--text-2)' }}>
              📍 {value.lat}, {value.lng}
            </span>
            <button
              type="button"
              className="btn-icon"
              title="Убрать точку"
              style={{ width: '24px', height: '24px' }}
              onClick={() => onChangeRef.current(null)}
            >
              <X size={12} />
            </button>
          </>
        ) : (
          <span className="text-dim" style={{ fontSize: '12px' }}>
            Кликните на карту, чтобы поставить точку
          </span>
        )}
      </div>
    </div>
  );
};
